// AgentActivityTrail — one turn's tool activity as a trail line. The wire →
// view-model seam: fold the entry's steps into groups (the pure
// `selectToolActivity`), localize each group's verbs, then hand the model to
// `ToolActivityView`. `activityModel` is exported so a merged trail line can
// name this activity in its own summary.
//
// LIVE vs SETTLED (D51): while the turn is in flight, a call that is still
// running belongs to the chat's live row ("Reading the source: X"), not here,
// so the trail lists only FINISHED steps. Once the turn closes, every step
// shows (a call that never settled stays visible as running, honestly).

import type { I18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { AgentActivityEntry } from "../types.js";
import {
    selectToolActivity,
    type ToolActivityActions,
    type ToolCallItemSource,
} from "./selectToolActivity.js";
import {
    collapsedSummary,
    ToolActivitySteps,
    ToolActivityView,
    type ToolActivityViewModel,
    type ToolCallItem,
} from "./ToolActivityView.js";

export const activityModel = (
    entry: AgentActivityEntry,
    inFlight: boolean,
    actions: ToolActivityActions,
    i18n: I18n,
): ToolActivityViewModel => {
    const localize = ({
        errorReason,
        resultLine,
        ...rest
    }: ToolCallItemSource): ToolCallItem => {
        const item =
            resultLine === undefined
                ? rest
                : { ...rest, resultLine: i18n._(resultLine) };
        return errorReason === undefined
            ? item
            : { ...item, errorReason: i18n._(errorReason) };
    };
    return {
        groups: selectToolActivity(
            inFlight
                ? entry.steps.filter((step) => step.type === "tool_done")
                : entry.steps,
            actions,
        ).map((group) => {
            const [first, ...rest] = group.items;
            const { pastCounted } = group.verb;
            const verb = {
                present: i18n._(group.verb.present),
                past: i18n._(group.verb.past),
            };
            return {
                id: group.id,
                verb:
                    pastCounted === undefined
                        ? verb
                        : {
                              ...verb,
                              pastCounted: (count: number) =>
                                  i18n._(pastCounted(count)),
                          },
                items: [localize(first), ...rest.map(localize)],
            };
        }),
    };
};

// The activity's one-line summary, localized ("" when nothing finished yet).
export const activitySummary = (
    model: ToolActivityViewModel,
    i18n: I18n,
): string => {
    const summary = collapsedSummary(model.groups);
    return typeof summary === "string" ? summary : i18n._(summary);
};

export const AgentActivityTrail = ({
    entry,
    inFlight = false,
    open = false,
    actions,
}: {
    readonly entry: AgentActivityEntry;
    readonly inFlight?: boolean;
    // Already inside an opened trail line: show the steps themselves, not a
    // second line to open (pen "Done – open": one click shows the steps).
    readonly open?: boolean;
    readonly actions: ToolActivityActions;
}): ReactNode => {
    const { i18n } = useLingui();
    const model = activityModel(entry, inFlight, actions, i18n);
    return open ? (
        <ToolActivitySteps groups={model.groups} standalone />
    ) : (
        <ToolActivityView model={model} />
    );
};
